// src/setupAxios.js
import axios from "axios";
import { urlBase } from "./constants/defaultValues";
import { getCurrentUser } from "./helpers/Utils";

// key -> { promise, resolve, reject } de la petición "líder" en curso
const inFlightRequests = new Map();

function buildKey(config) {
  return `${config.method}-${config.url}?${JSON.stringify(config.params || {})}`;
}

export default function setupAxios() {
  axios.defaults.baseURL = urlBase;
  axios.defaults.headers.common["Content-Type"] = "application/json";

  // Interceptor para evitar peticiones duplicadas
  axios.interceptors.request.use(
    (config) => {
      // 🔑 Clave única basada en método, URL y parámetros
      const key = buildKey(config);

      // 🚫 Excluir específicamente esta URL completa (sin importar baseURL)
      const requestUrl = `${config.baseURL ? config.baseURL.replace(/\/$/, "") : ""}${config.url}`;
      const isEgoiBanners =
        requestUrl.includes("https://api-rest.egoi.com.co/api/v1/banners") &&
        (config.params?.option === "web" ||
          config.url.includes("option=web"));

      if (isEgoiBanners) {
        // 🔓 Permitir repeticiones sin bloqueo
        return config;
      }

      // ✅ Si ya hay una petición igual en curso, reutilizar su respuesta
      // en vez de descartar esta (antes se perdía para siempre con
      // `new Promise(() => {})`, dejando el segundo caller colgado si la
      // primera petición tardaba más, p. ej. categorías vs marcas).
      const existing = inFlightRequests.get(key);
      if (existing) {
        config.adapter = () => existing.promise;
        return config;
      }

      let resolve, reject;
      const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });
      inFlightRequests.set(key, { promise, resolve, reject });
      config.__dedupeKey = key;
      config.__isDedupeLeader = true;

      // Agregar token automáticamente si existe usuario
      const user = getCurrentUser?.();
      if (user && user.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Interceptor de respuesta
  axios.interceptors.response.use(
    (response) => {
      if (response.config.__isDedupeLeader) {
        const key = response.config.__dedupeKey;
        const entry = inFlightRequests.get(key);
        inFlightRequests.delete(key);
        entry?.resolve(response);
      }
      return response;
    },
    (error) => {
      if (error.config?.__isDedupeLeader) {
        const key = error.config.__dedupeKey;
        const entry = inFlightRequests.get(key);
        inFlightRequests.delete(key);
        entry?.reject(error);
      }

      return Promise.reject(error);
    }
  );
}
